import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Compass, X, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ProfileDetailsForm } from "@/components/ProfileDetailsForm";

// Blocking first-run popup until the user has a designation and date of joining.
export const ProfileOnboarding = () => {
    const { user, checkAuth } = useAuth();
    const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("odyssey.onboarding.skip") === "1");
    if (!user?.user_id || (user.designation && user.date_of_joining)) return null;
    const optional = user.role === "super_admin";
    if (optional && dismissed) return null;
    const skip = () => { sessionStorage.setItem("odyssey.onboarding.skip", "1"); setDismissed(true); };
    const first = (user.name || "").split(" ")[0];

    return createPortal(
        <div className="fixed inset-0 z-[1100] overflow-y-auto bg-slate-950/70 backdrop-blur-md" data-testid="profile-onboarding">
            <div className="min-h-full flex items-center justify-center p-4">
            <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-[#0B1416] border border-white/10 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
                {optional && (
                    <button onClick={skip} aria-label="Close" data-testid="profile-onboarding-close" className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors">
                        <X className="h-4 w-4" />
                    </button>
                )}
                <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#2EB4AC]/25 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-28 -left-20 h-56 w-56 rounded-full bg-amber-400/10 blur-3xl" />
                <div className="relative p-6 sm:p-8">
                    <div className="h-12 w-12 rounded-2xl bg-[#2EB4AC] flex items-center justify-center shadow-[0_0_40px_-6px_rgba(46,180,172,0.9)]">
                        <Compass className="h-6 w-6 text-white" />
                    </div>
                    <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-[#2EB4AC] font-bold">Welcome aboard Odyssey</div>
                    <h2 className="mt-2 font-heading text-3xl font-bold text-white leading-tight">
                        {first ? `Hey ${first}, ` : ""}let's set up your profile
                    </h2>
                    <p className="mt-2 text-sm text-white/60">
                        Your designation and joining date appear on your Timeline and the Org chart. It takes 10 seconds.
                    </p>
                    {!optional && (
                        <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-400/10 border border-amber-300/30 px-3 py-2 text-xs font-semibold text-amber-200" data-testid="profile-onboarding-note">
                            <Lock className="h-3.5 w-3.5" /> Fill this to proceed
                        </div>
                    )}
                    <div className="mt-6">
                        <ProfileDetailsForm user={user} dark requireAll submitLabel="Continue to Odyssey" onSaved={() => checkAuth()} />
                    </div>
                    {optional && (
                        <button onClick={skip} data-testid="profile-onboarding-skip" className="mt-3 w-full text-center text-xs text-white/50 hover:text-white/80 transition-colors">
                            Skip for now
                        </button>
                    )}
                </div>
            </div>
            </div>
        </div>,
        document.body,
    );
};
